import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Button, Snackbar, CircularProgress, Dialog, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SurveyDetailsTemplate from '../services/surveyTemplate';
import './SurveyResponsePage.css';
import apiService from '../services/api';

const surveyFlowLogo = apiService.getBlobAssetUrl('Survey_Feedback.svg');
const criticalRiverText = apiService.getBlobAssetUrl('critical-river_final 1.svg');
const checkImg = apiService.getBlobAssetUrl('Tick_12.svg');
const copyRight = apiService.getBlobAssetUrl('CopyRight.svg');
const client = apiService.getBlobAssetUrl('client_icon.svg');

const SurveyResponsePage = () => {
  const { state, search } = useLocation();
  const navigate = useNavigate();
  const { uniqueId: surveyIdFromParams } = useParams();
  const queryParams = new URLSearchParams(search);
  const uniqueId = surveyIdFromParams || state?.uniqueId || queryParams.get('uniqueId');
  const [userResponses, setUserResponses] = useState({});
  const [surveyData, setSurveyData] = useState(null);
  const [questionTypes, setQuestionTypes] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [unansweredQuestions, setUnansweredQuestions] = useState([]);
  const [firstUnansweredQuestionId, setFirstUnansweredQuestionId] = useState(null);
  const [averageRating, setAverageRating] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const ratingBoxRef = useRef(null);
  const [isAlreadySubmitted, setIsAlreadySubmitted] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [sectionScores, setSectionScores] = useState([]);
  const containerRef = useRef(null);
  const hasInitializedSection = useRef(false);

  useEffect(() => {
    const header = document.querySelector('.survey-response-header-sticky');
    const headerHeight = header ? header.offsetHeight : 180;
    window.scrollTo({ top: headerHeight, behavior: 'smooth' });
  }, [currentSectionIndex]);

  const isQuestionAnswered = (response) => {
    return (
      response !== undefined &&
      response !== '' &&
      (!Array.isArray(response) || response.length > 0)
    );
  };

  const isCommentRequired = (question, responseText) => {
    const isParagraph = ['paragraph', 'short answer'].includes(question.questionType);
    return isParagraph && (!responseText || responseText.length === 0);
  };

  const processQuestion = (question, userResponses, displaySectionIndex, questionIndex) => {
    const userResponse = userResponses[question.questionId];
    const response = userResponse?.response;
    const responseText = typeof response === 'string' ? response.trim() : '';

    const isAnswered = isQuestionAnswered(response);
    const needsComment = isCommentRequired(question, responseText);

    if (!isAnswered || needsComment) {
      return {
        questionId: question.questionId,
        text: `Q${displaySectionIndex + 1}.${questionIndex + 1}. ${question.text}${needsComment ? ' (Comment Required)' : ''}`,
      };
    }

    return null;
  };

  const findUnansweredQuestions = (sections, userResponses) => {
    const unanswered = [];
    let firstUnanswered = null;

    // Create sorted sections array with display order
    const sortedSections = [...sections].sort((a, b) => {
      const aWeightage = parseFloat(a.weightage || 0);
      const bWeightage = parseFloat(b.weightage || 0);

      // Sections with 0 weightage go to the end
      if (aWeightage === 0 && bWeightage !== 0) return 1;
      if (aWeightage !== 0 && bWeightage === 0) return -1;

      // Maintain original order for sections with same weightage category
      return 0;
    });

    sortedSections.forEach((section, displaySectionIndex) => {
      let questionCounter = 0; // Initialize question counter for each section
      for (const measure of section.subsections || []) {
        for (const question of measure.questions || []) {
          questionCounter++;
          const questionInfo = processQuestion(question, userResponses, displaySectionIndex, questionCounter);
          if (questionInfo) {
            unanswered.push(questionInfo);
            if (!firstUnanswered) {
              firstUnanswered = questionInfo;
            }
          }
        }
      }
    });

    return { unanswered, firstUnanswered };
  };

  const buildResponseEntry = (question, userResponse, questionTypes) => {
    const questionTypeObj = getQuestionTypeObject(questionTypes, question);

    const responseEntry = {
      QuestionID: question.questionId,
      QuestionType: questionTypeObj?.ID || 1,
      QuestionTitle: question.text,
      ResponseText: Array.isArray(userResponse.response)
        ? userResponse.response.join(', ')
        : userResponse?.response?.toString() || '',
      Comments: userResponse?.comment || '',
    };

    Object.assign(responseEntry, getResponseOptionFields(question, userResponse));
    return responseEntry;
  };

  const extractQuestionResponses = (sections, userResponses, questionTypes) => {
    const responses = [];

    for (const section of sections) {
      for (const measure of section.subsections || []) {
        for (const question of measure.questions || []) {
          const userResponse = userResponses[question.questionId];
          if (!userResponse) continue;

          const responseEntry = buildResponseEntry(question, userResponse, questionTypes);
          responses.push(responseEntry);
        }
      }
    }

    return responses;
  };

  const getQuestionScore = (question, response) => {
    switch (question.questionType) {
      case 'multiple choice':
      case 'dropdown': {
        const selected = question.options.find(opt => opt.value === response);
        return selected?.weightage || 0;
      }
      case 'checkboxes': {
        const selectedOptions = question.options.filter(opt =>
          response.includes(opt.value)
        );
        return selectedOptions.reduce(
          (sum, opt) => sum + (opt.weightage || 0),
          0
        );
      }
      case 'linear scale':
        return Number(response) || 0;
      default:
        return 0;
    }
  };

  const getResponseOptionFields = (question, userResponse) => {
    const result = {};

    if (!userResponse?.response) return result;

    if (['multiple choice', 'checkboxes', 'dropdown'].includes(question.questionType)) {
      const selectedValue = Array.isArray(userResponse.response)
        ? userResponse.response
        : [userResponse.response];

      const selectedOptions = question.options.filter(opt =>
        selectedValue.includes(opt.value)
      );

      result.OptionIDs = selectedOptions
        .map(opt => opt.optionId)
        .filter(id => id !== undefined);
    } else if (question.questionType === 'linear scale') {
      result.OptionID = Number(userResponse.response);
    }

    return result;
  };

  const getQuestionTypeObject = (questionTypes, question) => {
    return questionTypes.find(
      type => type.Type.toLowerCase() === question.questionType
    ) || { ID: 1 };
  };

  const buildSurveyResponses = (surveyData, userResponses, questionTypes) => {
    const responses = extractQuestionResponses(surveyData.sections || [], userResponses, questionTypes);
    return responses;
  };

  const isAnswered = (response) =>
    response !== undefined &&
    response !== '' &&
    (!Array.isArray(response) || response.length > 0);

  const calculateSectionScore = (subsections, userResponses) => {
    let sectionScore = 0;
    let answeredInSection = 0;

    for (const measure of subsections || []) {
      for (const question of measure.questions || []) {
        const response = userResponses[question.questionId]?.response;
        if (!isAnswered(response)) continue;

        const score = getQuestionScore(question, response);
        if (score > 0) {
          sectionScore += score;
          answeredInSection += 1;
        }
      }
    }

    return { sectionScore, answeredInSection };
  };

  const calculateAllSectionScores = () => {
    if (!surveyData || !surveyData.sections) return [];

    return surveyData.sections
      .filter(section => {
        const weightage = parseFloat(section.weightage || 0);
        return weightage !== 0;
      })
      .map(section => {
        const { sectionScore, answeredInSection } = calculateSectionScore(section.subsections, userResponses);
        const avgScore = answeredInSection > 0 ? (sectionScore / answeredInSection).toFixed(2) : '0.00';
        const weightage = parseFloat(section.weightage || 0);
        const weightedAvg = ((avgScore * weightage) / 100).toFixed(2);
        return {
          title: section.title,
          score: avgScore,
          weightage: weightage,
          weightedAvg: weightedAvg
        };
      });
  };

  useEffect(() => {
    if (!surveyData || !surveyData.sections) return;

    let totalCSAT = 0;

    for (const section of surveyData.sections) {
      const { sectionScore, answeredInSection } = calculateSectionScore(section.subsections, userResponses);

      if (answeredInSection > 0) {
        const sectionAvg = sectionScore / answeredInSection;
        const weightPercent = parseFloat(section.weightage || 0) / 100;
        totalCSAT += sectionAvg * weightPercent;
      }
    }

    setAverageRating(totalCSAT.toFixed(2));
  }, [userResponses, surveyData]);

  // Initialize to first section in sorted order when survey data loads
  useEffect(() => {
    if (surveyData && surveyData.sections && surveyData.sections.length > 0 && !hasInitializedSection.current) {
      const sortedSections = [...surveyData.sections].sort((a, b) => {
        const aWeightage = parseFloat(a.weightage || 0);
        const bWeightage = parseFloat(b.weightage || 0);

        // Sections with 0 weightage go to the end
        if (aWeightage === 0 && bWeightage !== 0) return 1;
        if (aWeightage !== 0 && bWeightage === 0) return -1;

        // Maintain original order for sections with same weightage category
        return 0;
      });

      // Find the original index of the first section in sorted order
      const firstSectionInDisplayOrder = sortedSections[0];
      const firstOriginalIndex = surveyData.sections.findIndex(
        s => s.sectionId === firstSectionInDisplayOrder.sectionId
      );

      if (firstOriginalIndex !== -1) {
        setCurrentSectionIndex(firstOriginalIndex);
        hasInitializedSection.current = true;
      }
    }
  }, [surveyData]);

  if (!uniqueId) {
    return (
      <Box sx={{ backgroundColor: '#FFFFFF', minHeight: '100vh', padding: '20px' }}>
        <Typography variant="h6" color="error">
          Survey ID not provided
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/surveys')}
          style={{
            backgroundColor: '#034589',
            color: 'white',
            textTransform: 'none',
            marginTop: '10px',
          }}
        >
          Back to Surveys
        </Button>
      </Box>
    );
  }

  const handleResponseChange = (questionId, value, type = 'response') => {
    setUserResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [type]: value,
      },
    }));
    setUnansweredQuestions(prev => prev.filter(q => q.questionId !== questionId));
    if (firstUnansweredQuestionId === questionId) {
      setFirstUnansweredQuestionId(null);
    }
  };

  const isCurrentSectionComplete = () => {
    if (!surveyData || !surveyData.sections[currentSectionIndex]) return false;

    const currentSection = surveyData.sections[currentSectionIndex];
    for (const measure of currentSection.subsections || []) {
      for (const question of measure.questions || []) {
        const response = userResponses[question.questionId]?.response;
        if (!isQuestionAnswered(response)) {
          return false;
        }
      }
    }
    return true;
  };

  const getSortedSections = () => {
    if (!surveyData || !surveyData.sections) return [];
    return [...surveyData.sections].sort((a, b) => {
      const aWeightage = parseFloat(a.weightage || 0);
      const bWeightage = parseFloat(b.weightage || 0);

      // Sections with 0 weightage go to the end
      if (aWeightage === 0 && bWeightage !== 0) return 1;
      if (aWeightage !== 0 && bWeightage === 0) return -1;

      // Maintain original order for sections with same weightage category
      return 0;
    });
  };

  const isLastSectionInDisplayOrder = () => {
    if (!surveyData || !surveyData.sections[currentSectionIndex]) return false;

    const sortedSections = getSortedSections();
    const currentSection = surveyData.sections[currentSectionIndex];
    const currentDisplayIndex = sortedSections.findIndex(s => s.sectionId === currentSection.sectionId);

    return currentDisplayIndex === sortedSections.length - 1;
  };

  const handleNextSection = () => {
    if (!surveyData || !surveyData.sections[currentSectionIndex]) return;

    const sortedSections = getSortedSections();
    const currentSection = surveyData.sections[currentSectionIndex];
    const currentDisplayIndex = sortedSections.findIndex(s => s.sectionId === currentSection.sectionId);

    if (currentDisplayIndex < sortedSections.length - 1) {
      const nextSection = sortedSections[currentDisplayIndex + 1];
      const nextOriginalIndex = surveyData.sections.findIndex(s => s.sectionId === nextSection.sectionId);
      setCurrentSectionIndex(nextOriginalIndex);
    }
  };

  const handlePreview = () => {
    if (!surveyData || !questionTypes || !surveyData.sections) {
      setSnackbarMessage('Survey not loaded yet. Please wait.');
      setSnackbarOpen(true);
      return;
    }

    const { unanswered, firstUnanswered } = findUnansweredQuestions(surveyData.sections || [], userResponses);

    if (unanswered.length > 0) {
      setUnansweredQuestions(unanswered);
      setFirstUnansweredQuestionId(firstUnanswered?.questionId || null);
      setSnackbarMessage(`Please answer: ${firstUnanswered?.text || 'all questions'}`);
      setSnackbarOpen(true);

      setTimeout(() => {
        const element = document.getElementById(`question-${firstUnanswered?.questionId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

      return;
    }

    const scores = calculateAllSectionScores();
    setSectionScores(scores);
    setPreviewDialogOpen(true);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const accessToken = process.env.REACT_APP_ACCESS_TOKEN;
      const responses = buildSurveyResponses(surveyData, userResponses, questionTypes);

      const finalPayload = {
        IsRespCompleted: 1,
        UniqueId: uniqueId,
        Responses: responses,
        AverageRating: Number(averageRating),
      };

      console.log('Posting to:', `${process.env.REACT_APP_API_BASE_URL}/submit_survey`);
      console.log('Payload structure:', JSON.stringify(finalPayload, null, 2));

      const response = await apiService.postData('submit_survey', finalPayload, accessToken);
      console.log('Survey saved successfully:', response);
      await apiService.fetchData('projects/surveycompleted');
      await apiService.fetchData('projects/statistics');
      setPreviewDialogOpen(false);
      setSubmissionComplete(true);
    } catch (error) {
      console.error('Error submitting survey:', error.response?.data || error.message);
      setSnackbarMessage('Failed to submit survey. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#FFFFFF', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div className="survey-response-header-sticky">
        <div className="survey-response-header">
          <img src={criticalRiverText} alt="Critical River Logo" className="survey-response-logo" style={{ width: '150px' }} />
          {surveyData?.name && (
            <Typography
              sx={{
                fontFamily: 'Mulish',
                fontWeight: 700,
                fontSize: '24px',
                color: '#013E7D',
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)'
              }}
            >
              {surveyData.name}
            </Typography>
          )}
          <img src={surveyFlowLogo} alt="Survey Flow Logo" className="survey-response-logo" />
        </div>
        <hr className="survey-response-divider" />
        {surveyData && surveyData.sections && !isAlreadySubmitted && !submissionComplete && (
          <Box sx={{ borderBottom: '2px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', gap: '0px', justifyContent: 'center' }}>
              {[...surveyData.sections]
                .sort((a, b) => {
                  const aWeightage = parseFloat(a.weightage || 0);
                  const bWeightage = parseFloat(b.weightage || 0);

                  // Sections with 0 weightage go to the end
                  if (aWeightage === 0 && bWeightage !== 0) return 1;
                  if (aWeightage !== 0 && bWeightage === 0) return -1;

                  // Maintain original order for sections with same weightage category
                  return 0;
                })
                .map((section) => {
                  const originalIndex = surveyData.sections.findIndex(s => s.sectionId === section.sectionId);
                  return (
                    <Box
                      key={section.sectionId}
                      onClick={() => setCurrentSectionIndex(originalIndex)}
                      sx={{
                        padding: '12px 24px',
                        cursor: 'pointer',
                        borderBottom: currentSectionIndex === originalIndex ? '3px solid #013E7D' : '3px solid transparent',
                        backgroundColor: currentSectionIndex === originalIndex ? '#F5F8FA' : 'transparent',
                        color: currentSectionIndex === originalIndex ? '#013E7D' : '#666',
                        fontWeight: currentSectionIndex === originalIndex ? 600 : 400,
                        transition: 'all 0.3s',
                        '&:hover': {
                          backgroundColor: '#F5F8FA'
                        }
                      }}
                    >
                      {section.title}
                    </Box>
                  );
                })}
            </Box>
          </Box>
        )}
      </div>
      <div
        className="survey-response-container"
        style={{
          flex: 1,
          marginTop: (submissionComplete || isAlreadySubmitted) ? '100px' : undefined,
          display: (submissionComplete || isAlreadySubmitted) ? 'flex' : undefined,
          flexDirection: (submissionComplete || isAlreadySubmitted) ? 'column' : undefined,
          justifyContent: (submissionComplete || isAlreadySubmitted) ? 'center' : undefined,
          alignItems: (submissionComplete || isAlreadySubmitted) ? 'center' : undefined,
        }}
      >
        {isAlreadySubmitted ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              flexDirection: 'column',
            }}
          >
            <img src={checkImg} alt="Submitted" style={{ width: 50, height: 50, marginBottom: '20px' }} />
            <Typography
              sx={{
                fontFamily: 'Mulish',
                fontWeight: 500,
                fontSize: '22px',
                color: '#d32f2f',
              }}
            >
              You have already responded
            </Typography>
          </Box>
        ) : submissionComplete ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#FFFFFF',
              padding: '30px',
            }}
          >
            <Box
              sx={{
                backgroundColor: '#fff',
                borderRadius: '16px',
                padding: '40px',
                maxWidth: '900px',
                width: '100%',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '40px',
              }}
            >
              <Box sx={{ flex: 1, textAlign: 'center' }}>
                <img
                  src={checkImg}
                  alt="Submitted"
                  style={{
                    width: '60px',
                    height: '60px',
                    marginBottom: '20px',
                    display: 'block',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: 'Mulish',
                    fontWeight: 700,
                    fontSize: '28px',
                    color: '#013E7D',
                    marginBottom: '15px',
                  }}
                >
                  Thank You for Sharing Your Feedback!
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Mulish',
                    fontWeight: 400,
                    fontSize: '16px',
                    color: '#333333',
                    marginBottom: '10px',
                  }}
                >
                  We truly appreciate you taking the time to help us improve.
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Mulish',
                    fontWeight: 400,
                    fontSize: '16px',
                    color: '#333333',
                    marginBottom: '10px',
                  }}
                >
                  Your input will be carefully reviewed to ensure we continue to meet — and exceed — your expectations.
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Mulish',
                    fontWeight: 500,
                    fontSize: '16px',
                    color: '#4CAF50',
                  }}
                >
                  Your voice helps us build stronger partnerships and deliver greater value.
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <img
                  src={client}
                  alt="Client illustration"
                  style={{
                    width: '180px',
                    height: '180px',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            </Box>
          </Box>
        ) : (
          <>
            <SurveyDetailsTemplate
              uniqueId={uniqueId}
              userResponses={userResponses}
              onResponseChange={handleResponseChange}
              setSurveyData={setSurveyData}
              setQuestionTypes={setQuestionTypes}
              surveyData={surveyData}
              questionTypes={questionTypes}
              unansweredQuestions={unansweredQuestions}
              firstUnansweredQuestionId={firstUnansweredQuestionId}
              onAlreadySubmitted={() => setIsAlreadySubmitted(true)}
              currentSectionIndex={currentSectionIndex}
            />
            <div className="survey-response-buttons">
              {surveyData && questionTypes.length > 0 && (
                <>
                  {!isLastSectionInDisplayOrder() ? (
                    <Button
                      variant="contained"
                      onClick={handleNextSection}
                      disabled={!isCurrentSectionComplete()}
                      style={{
                        backgroundColor: isCurrentSectionComplete() ? '#034589' : '#ccc',
                        color: 'white',
                        textTransform: 'none',
                        padding: '10px',
                        height: '43px',
                        width: '200px',
                        borderRadius: '6px',
                        cursor: isCurrentSectionComplete() ? 'pointer' : 'not-allowed',
                      }}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handlePreview}
                      disabled={isLoading}
                      style={{
                        backgroundColor: '#034589',
                        color: 'white',
                        textTransform: 'none',
                        padding: '10px',
                        height: '43px',
                        width: '200px',
                        borderRadius: '6px',
                      }}
                    >
                      Preview
                    </Button>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
      <hr className="survey-response-divider" />
      <div className="survey-response-footer-container">
        <div className="survey-response-footer-branding">
          <img
            src={copyRight}
            alt="logo"
            style={{ height: '18px', marginRight: '1px', verticalAlign: 'middle' }}
          />
          <span className="powered-by-text">{new Date().getFullYear()} CriticalRiver. All Rights Reserved</span>
        </div>
      </div>
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#013E7D', color: 'white', fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Section Wise Score Summary
          <IconButton
            onClick={() => setPreviewDialogOpen(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '30px' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '16px' }}>Section</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '16px' }}>Average Score</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '16px' }}>Weightage (%)</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '16px' }}>Weighted Average</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sectionScores.map((section, index) => (
                <TableRow key={index}>
                  <TableCell>{section.title}</TableCell>
                  <TableCell>{section.score}</TableCell>
                  <TableCell>{section.weightage}</TableCell>
                  <TableCell>{section.weightedAvg}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ backgroundColor: '#F5F8FA' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '16px' }}>Total CSAT Score</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '16px', color: '#013E7D' }}>{averageRating}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '30px' }}>
            <Button
              variant="outlined"
              onClick={() => {
                setPreviewDialogOpen(false);
                setCurrentSectionIndex(0);
              }}
              disabled={isLoading}
              sx={{
                textTransform: 'none',
                borderColor: '#013E7D',
                color: '#013E7D',
                '&:hover': {
                  borderColor: '#013E7D',
                  backgroundColor: '#F5F8FA'
                }
              }}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isLoading}
              sx={{
                backgroundColor: '#034589',
                color: 'white',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#013E7D'
                }
              }}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} style={{ marginRight: '8px', color: 'white' }} />
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        ContentProps={{
          sx: { backgroundColor: '#FF0000', color: '#FFFFFF' },
        }}
      />
    </Box>
  );
};

export default SurveyResponsePage;